import _ from 'underscore';
import Promise from 'bluebird';
import { useEffect, useCallback, useMemo, useRef, useContext } from 'react';
import { PermissionsContext } from '@bingads-webui-react/hoc-with-permissions';

import { loadImageByUrl } from './load-image-by-url';
import { getPlacementListMetaData } from './placement-meta-list';
import { useAdPreviewReducer } from './use-ad-preview-reducer';

import { getCentralCroppedPlacements } from './get-central-crop-settings';

export const pickSingleImageFromImageList = (images) => {
  // try to find the included placement first
  let selectedImage = _.find(images, img => img && img.isPlacementIncluded && img.imageMeta);
  // try to find a valid image if no placement is included
  if (!selectedImage) {
    selectedImage = _.find(images, img => img && img.imageMeta);
  }
  if (!selectedImage) {
    selectedImage = _.first(images);
  }
  return selectedImage;
};

// const getImageInfo = (image: string | TImageParam) : Promise<{
//   imageMeta: TPreviewImageMeta,
//   imageCropping: TCropRect | undefined | null,
//   manualPlacementCroppings: Array<TPlacementCroppingParam> | undefined | null,
// }> => {
const getImageInfo = (image, onError) => {
  // if image is url, load it to get image info
  const pLoadedImage = _.isString(image)
    ? loadImageByUrl(image).then(({ src, width, height }) => ({
      url: src, width, height, originalUrl: src, originalWidth: width, originalHeight: height,
    }))
    : Promise.resolve(image);

  const p$ret = pLoadedImage.then((loadedImage) => {
    const {
      id, url, width, height, originalUrl, originalWidth, originalHeight, imageCropping, placementsCropping, roiInfo, expandedMeta,
    } = loadedImage || {};

    // get image meta info, including crop settings
    const imageMeta = {
      id, url, width, height, originalUrl, originalWidth, originalHeight, roiInfo, expandedMeta,
    };
    return { imageMeta, imageCropping, manualPlacementCroppings: placementsCropping };
  }).catch((err) => {
    if (onError) {
      onError(err);
    }
    return null;
  });

  return p$ret;
};

// function getPlacementSet(
//   imageMeta: TPreviewImageMeta,
//   imageCropping: TCropRect | undefined | null,
//   manualPlacementCroppings: Array<TPlacementCroppingParam> | undefined | null,
//   placementSetParam: TPlacementSetParam
// ) : Promise<TPlacementSet> {
function getPlacementSet(imageMeta, imageCropping, manualPlacementCroppings, placementSetParam, isAdPreviewRefreshEnabled) {
  //
  const { placementSetName, placementTypes, getDefaultPlacementsCropping = getCentralCroppedPlacements } = placementSetParam;

  // get placements' meta info (predefined constants) for selected placements
  const placementMetaList = getPlacementListMetaData(placementTypes, isAdPreviewRefreshEnabled);

  // get default placement crop settings, it could be async or sync call
  return Promise.resolve(getDefaultPlacementsCropping({ imageMeta, /* maxCropBox */imageCropping, placementMetaList }))
    .then((defaultCroppingList = []) => {
      // merge placements meta info with crop settings (default or manual placement crop settings)
      const placementList = _.map(placementMetaList, (placementMeta, index) => {
        // manual placement crop settings (set by users) on UI take higher priority
        const { cropping: manualCropping } = _.find(manualPlacementCroppings, item => item.placementType === placementMeta.placementType) || {};
        const {
          placementCropping, expandedImageHeight, expandedImageWidth, expandedImageUrl, expandedImageCropSettings,
        } = defaultCroppingList[index] || {};
        return ({
          placementMeta,
          placementCropping: manualCropping || placementCropping,

          // set initial value to true if there is manual cropping for this placement
          // the purpose is to inlucde this placement when sending crop MT api request
          hasCroppingChanged: !!manualCropping,
          expandedImageUrl: manualCropping ? undefined : expandedImageUrl,
          expandedImageHeight: manualCropping ? undefined : expandedImageHeight,
          expandedImageWidth: manualCropping ? undefined : expandedImageWidth,
          expandedImageCropSettings: manualCropping ? undefined : expandedImageCropSettings,
        });
      });
      return { name: placementSetName, placementList };
    });
}

// function getImageAndPlacementSetList(
//   imageMeta: TPreviewImageMeta,
//   imageCropping: TCropRect | undefined | null,
//   manualPlacementCroppings: Array<TPlacementCroppingParam> | undefined | null,
//   placementSetsParam: Array<TPlacementSetParam>
// ) : Promise<TImageAndPlacementSets> {
function getImageAndPlacementSetList({
  imageMeta, imageCropping, manualPlacementCroppings, placementSetsParam, isAdPreviewRefreshEnabled,
}) {
  const ret = Promise.map(
    placementSetsParam,
    param => getPlacementSet(imageMeta, imageCropping, manualPlacementCroppings, param, isAdPreviewRefreshEnabled),
    { concurrency: 5 }
  ).then((placementSets) => {
    const result = {
      previewImage: {
        imageMeta, // todo: update imageMeta.width & height using image cropping? is it needed?
        imageCropping,

        // set initial value to true if there is manual cropping for this image
        // the purpose is to inlucde this cropping when sending crop MT api request
        hasCroppingChanged: !!imageCropping,
      },
      placementSets,
    };
    return result;
  });
  return ret;
}

// todo: add a simplified interface to support single placement set
// export const useAdPreviewModel = (placementConfig: Array<UAPPlacementConfigParam> | undefined | null, onError) => {
export const useAdPreviewModel = (placementConfig, onError, { patchPlacementList } = {}) => {
  //
  // keep a reference to the current model config
  const curPlacementConfigRef = useRef(placementConfig);

  const permissions = useContext(PermissionsContext);
  const isAdPreviewRefreshEnabled = _.get(permissions, ['dynamic', 'AdPreviewRefresh']);

  const {
    previewImageListState, // Array<UAPImageAndPlacementSets>
    hasDataChanged,
    resetReducerStates,
    updateImageCropping: updateImageCroppingState,
    updatePlacementCropping: updatePlacementCroppingState,
    updatePlacementCroppingByImageType: updatePlacementCroppingByImageTypeState,
    activeImageIndices,
    setActiveImageIndices,
  } = useAdPreviewReducer();

  const updateImageCropping = useCallback((newImageCropping, imageIndex = 0) => {
    // get image to update
    const { previewImage } = (previewImageListState && previewImageListState[imageIndex]) || {};
    const { imageMeta, imageCropping } = previewImage || {};

    // get placement set param
    const { placementSets } = (curPlacementConfigRef.current && curPlacementConfigRef.current[imageIndex]) || {};

    if (placementSets && imageMeta && newImageCropping && !_.isEqual(imageCropping, newImageCropping)) {
      getImageAndPlacementSetList({
        imageMeta, imageCropping: newImageCropping, manualPlacementCroppings: undefined, placementSetsParam: placementSets, isAdPreviewRefreshEnabled,
      }).then((imgAndPlacementSets) => {
        updateImageCroppingState(imgAndPlacementSets, imageIndex);
      });
    }
  }, [previewImageListState, updateImageCroppingState]);

  // const updatePlacementCropping = useCallback((params: {
  //   newPlacementCropping: TCropRect,
  //   placementSetName: string,
  //   placementType: EPlacementType,
  //   imageIndex: number,
  // }) => {
  const updatePlacementCropping = useCallback((params) => {
    const {
      newPlacementCropping, placementSetName, placementType, imageIndex = 0,
    } = params || {};

    updatePlacementCroppingState({
      imageIndex, placementSetName, placementType, cropping: newPlacementCropping,
    });
  }, [updatePlacementCroppingState]);

  // const updatePlacementCroppingByImageType = useCallback((params: {
  //   newPlacementCropping: TCropRect,
  //   placementSetName: string,
  //   imageType: EImageType,
  //   imageIndex: number,
  // }) => {
  const updatePlacementCroppingByImageType = useCallback((params) => {
    const {
      newPlacementCropping, placementSetName, imageType, imageIndex = 0,
    } = params || {};

    updatePlacementCroppingByImageTypeState({
      imageIndex, placementSetName, imageType, cropping: newPlacementCropping,
    });
  }, [updatePlacementCroppingByImageTypeState]);

  // to avoid re-initialize model using same placement config
  const prevModelConfigRef = useRef(null);

  // model config could be set by using this method, or passing it in hook parameters
  // const initializeModel = useCallback((pNewModelConfig: Array<TPlacementConfigParam> | null | undefined) => {
  const initializeModel = useCallback((pNewModelConfig) => {
    //
    // remove invalid entries
    const newModelConfig = _.compact(pNewModelConfig);
    if (_.isEmpty(newModelConfig)) {
      return;
    }

    // avoid initializing again using same config
    if (_.isEqual(prevModelConfigRef.current, newModelConfig)) {
      return;
    }
    prevModelConfigRef.current = newModelConfig;

    // keep a reference to the current model config
    curPlacementConfigRef.current = newModelConfig;

    const [firstConfig, ...otherConfigs] = newModelConfig;

    // load first image first and then others, to unblock UI rendering
    getImageInfo(firstConfig.image, onError).then((firstImageInfo) => {
      // stop if the 1st image is invalid
      if (!firstImageInfo) {
        return null;
      }
      const { imageMeta, imageCropping, manualPlacementCroppings } = firstImageInfo;
      // set the partial loaded image data (without placement cropping), to unblock UI rendering (such as UI only needs image info)
      const partialFirstPreviewImage = {
        previewImage: { imageMeta, imageCropping, hasCroppingChanged: !!imageCropping },
        placementSets: [],
      };
      resetReducerStates({ previewImageListState: [partialFirstPreviewImage] });

      // get placement sets for the first image
      return getImageAndPlacementSetList({
        imageMeta, imageCropping, manualPlacementCroppings, placementSetsParam: firstConfig.placementSets, isAdPreviewRefreshEnabled,
      }).then((firstPreviewImage) => {
        if (firstPreviewImage) {
          // set the state for the first image first, so UI can render something quickly
          if (patchPlacementList) {
            patchPlacementList(firstPreviewImage);
          }
          resetReducerStates({ previewImageListState: [firstPreviewImage] });
          // get image and placement sets for other images
          if (!_.isEmpty(otherConfigs)) {
            Promise.map(
              otherConfigs,
              config => getImageInfo(config.image, onError).then((imgInfo) => {
                if (imgInfo) {
                  return getImageAndPlacementSetList({
                    imageMeta: imgInfo.imageMeta,
                    imageCropping: imgInfo.imageCropping,
                    manualPlacementCroppings: imgInfo.manualPlacementCroppings,
                    placementSetsParam: config.placementSets,
                    isAdPreviewRefreshEnabled,
                  });
                }
                return null;
              }),
              { concurrency: 5 }
            ).then((ret /* Array<TImageAndPlacementSets> */) => {
              // set all the images now
              const otherPreviewImages = _.compact(ret);
              if (!_.isEmpty(otherPreviewImages)) {
                resetReducerStates({ previewImageListState: [firstPreviewImage, ...otherPreviewImages] });
              }
            });
          }
        }
      });
    });
  }, [isAdPreviewRefreshEnabled, onError, patchPlacementList, resetReducerStates]);

  useEffect(() => {
    if (!_.isEmpty(placementConfig)) {
      initializeModel(placementConfig);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placementConfig]);

  //
  // transform model state to make it easier to use for rendering components
  // const multiImagePlacementSetsViewModel = useMemo((): Array<UAPVMPlacementSet> | null => {
  const getPlacementSetsViewModel = useCallback((activeImagesOnly) => {
    if (_.isEmpty(previewImageListState)) {
      return [];
    }

    const finalPlacementSets = [];

    // only get active images only
    let pickedPreviewImageListState = previewImageListState;
    if (activeImagesOnly && !_.isEmpty(activeImageIndices)) {
      pickedPreviewImageListState = _.compact(_.map(activeImageIndices, idx => previewImageListState[idx]));
    }

    //
    // each image in model state
    //
    _.each(pickedPreviewImageListState, ({ previewImage: pImage, placementSets }) => {
      const { imageMeta, imageCropping, imageSmartEffect } = pImage || {};
      //
      // each placement set
      //
      _.each(placementSets, ({ name: placementSetName, placementList }) => {
        // create a placement set view model if not exist
        let placementSetViewModel = finalPlacementSets.find(item => item.placementSetName === placementSetName);
        if (!placementSetViewModel) {
          placementSetViewModel = { placementSetName, placementList: [] };
          finalPlacementSets.push(placementSetViewModel);
        }
        //
        // for each placement
        //
        _.each(placementList, ({
          placementMeta, placementCropping, placementSmartEffect, isPlacementIncluded, expandedImageUrl, expandedImageHeight, expandedImageWidth, expandedImageCropSettings, ...rest
        }) => {
          const imageViewModel = {
            imageMeta,
            imageCropping,
            imageSmartEffect,
            placementCropping,
            placementSmartEffect,
            effectiveImageUrl: (placementSmartEffect && placementSmartEffect.effectSrc) || (imageSmartEffect && imageSmartEffect.effectSrc) || expandedImageUrl || (imageMeta && (imageMeta.originalUrl || imageMeta.url)),
            effectiveImageWidth: expandedImageWidth || (imageMeta && imageMeta.originalWidth),
            effectiveImageHeight: expandedImageHeight || (imageMeta && imageMeta.originalHeight),
            expandedPlacementCropping: expandedImageCropSettings,
            isPlacementIncluded,
            ...rest,
          };
          const fpIndex = _.findIndex(placementSetViewModel.placementList, (item => item.placementMeta.placementType === placementMeta.placementType));
          if (fpIndex > -1) {
            placementSetViewModel.placementList[fpIndex].images.push(imageViewModel);
            placementSetViewModel.placementList[fpIndex].isPlacementIncluded
              = isPlacementIncluded || placementSetViewModel.placementList[fpIndex].isPlacementIncluded;
          } else {
            placementSetViewModel.placementList.push({
              placementMeta, isPlacementIncluded, images: [imageViewModel],
            });
          }
        });
      });
    });

    return finalPlacementSets;
  }, [activeImageIndices, previewImageListState]);

  const multiImagePlacementSetsViewModel = useMemo(() => getPlacementSetsViewModel(false), [getPlacementSetsViewModel]);
  const activeMultiImagePlacementSetsViewModel = useMemo(() => getPlacementSetsViewModel(true), [getPlacementSetsViewModel]);

  /**
   * list of placements to shown, this is a simplified interface for signle image mode
   * return type: Array<UAPVMSingleImagePlacementSet>
   */
  const singleImagePlacementSetsViewModel = useMemo(() => {
    const ret = _.map(multiImagePlacementSetsViewModel, ({ placementSetName, placementList }) => {
      const ret2 = _.map(placementList, ({ placementMeta, isPlacementIncluded, images }) => {
        const selectedImage = pickSingleImageFromImageList(images);
        return {
          placementMeta,
          isPlacementIncluded,
          ...selectedImage,
        };
      });
      return { placementSetName, placementList: ret2 };
    });
    return ret || [];
  }, [multiImagePlacementSetsViewModel]);

  const activeSingleImagePlacementSetsViewModel = useMemo(() => {
    const ret = _.map(activeMultiImagePlacementSetsViewModel, ({ placementSetName, placementList }) => {
      const ret2 = _.map(placementList, ({ placementMeta, isPlacementIncluded, images }) => {
        const selectedImage = pickSingleImageFromImageList(images);
        return {
          placementMeta,
          isPlacementIncluded,
          ...selectedImage,
        };
      });
      return { placementSetName, placementList: ret2 };
    });
    return ret || [];
  }, [activeMultiImagePlacementSetsViewModel]);

  const firstImage = useMemo(() => {
    const { previewImage } = _.first(previewImageListState) || {};
    return previewImage;
  }, [previewImageListState]);

  const allImages = useMemo(() => {
    const imgs = _.map(previewImageListState, ({ previewImage }) => previewImage);
    return imgs || [];
  }, [previewImageListState]);

  const activeImages = useMemo(() => {
    if (_.isEmpty(activeImageIndices)) {
      return [];
    }
    const ret = _.map(activeImageIndices, index => previewImageListState[index] && previewImageListState[index].previewImage);
    return _.compact(ret) || [];
  }, [previewImageListState, activeImageIndices]);

  // get changed image cropping and placement croppings
  // const changedCropSettings: Array<{ image: TPreviewImage, placements: Array<TPlacement> }> = useMemo(() => {
  const changedCropSettings = useMemo(() => {
    // each image in model state
    const ret = _.map(previewImageListState, ({ previewImage, placementSets }) => {
      const image = { ...previewImage };
      const placements = [];
      // each placement set
      _.each(placementSets, ({ placementList }) => {
        // for each placement
        _.each(placementList, (placement) => {
          if (placement.hasCroppingChanged) {
            placements.push(placement);
          }
        });
      });
      return { image, placements };
    });
    return ret || [];
  }, [previewImageListState]);

  const resetReducerStatesWrapper = useCallback((newState) => {
    resetReducerStates(newState);
    prevModelConfigRef.current = null;
  }, [resetReducerStates]);

  return {
    // previewImageListState,
    activeImageIndices,
    setActiveImageIndices,
    resetReducerStates: resetReducerStatesWrapper,
    //
    setPlacementConfig: initializeModel, // todo: support partial update? only update one image? or one placement set?
    updateImageCropping,
    updatePlacementCropping,
    updatePlacementCroppingByImageType,
    multiImagePlacementSets: activeMultiImagePlacementSetsViewModel,
    singleImagePlacementSets: activeSingleImagePlacementSetsViewModel,
    multiImagePlacementSetsViewModel,
    singleImagePlacementSetsViewModel,
    firstImage,
    allImages,
    activeImages,
    hasDataChanged,
    changedCropSettings,
  };
};
